/**
 * Prefetch utility for lazy-loaded route components
 * Helps eliminate lazy loading delays by preloading components in the background
 */

/**
 * Prefetch a component by calling its import function
 * Downloads the JavaScript bundle in the background to eliminate lazy loading delay
 *
 * @param {Function} componentImportFn - The component's dynamic import function
 * @returns {Promise<void>} Resolves when prefetch completes or fails gracefully
 *
 * @example
 * // Prefetch by passing the component import function
 * prefetchComponent(() => import('@/components/SomeComponent.vue'))
 */
export function prefetchComponent(componentImportFn) {
  if (!componentImportFn || typeof componentImportFn !== 'function') {
    return Promise.resolve()
  }

  return componentImportFn().catch(() => {
    // Silently fail - component will load on-demand if prefetch fails
  })
}

/**
 * Prefetch multiple components at once
 * Useful for preloading frequently accessed components in parallel
 *
 * @param {Array<Function>} componentImportFns - Array of component import functions
 * @returns {Promise<void>}
 *
 * @example
 * prefetchMultipleComponents([
 *   () => import('@/components/Editor.vue'),
 *   () => import('@/components/Templates.vue'),
 *   () => import('@/components/Reports.vue'),
 * ])
 */
export function prefetchMultipleComponents(componentImportFns) {
  if (!Array.isArray(componentImportFns)) {
    return Promise.resolve()
  }

  return Promise.allSettled(
    componentImportFns.map((fn) => prefetchComponent(fn)),
  )
}
